import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { TableProps } from 'antd';
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Table,
} from 'antd';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { styled, useTheme } from 'styled-components';
import { useTranslate } from '@/translations';
import { AppSearch } from '@/types';
import { StorageKeys } from '@/utils/constants';
import { FilterDropdownProps, SortOrder } from 'antd/es/table/interface';
import { baseSort } from '@/utils/helpers';
import {
  faAdd,
  faArrowDown,
  faArrowUp,
  faCalendar,
  faFilter,
  faSearch,
  Icon,
} from '../Icon';
import { TableExportButton } from './TableExportButton';
import { CustomColumns, RenderType, TableExportOptions } from './types';
import { QuickViewColumns, QuickViewPanel } from './QuickViewPanel';
import { EditableRow } from './EditableRow';
import { EditableCell } from './EditableCell';
import {
  DeferredInlineEditProvider,
  DeferredInlineEditUpdate,
  useDeferredInlineEdit,
} from './InlineEditApplyContext';

dayjs.extend(isBetween);

const HeaderToolbar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  margin-bottom: 1rem;
  gap: 12px;

  @media (max-width: 991px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderSide = styled.div<{ $side: 'left' | 'right' }>`
  display: flex;
  align-items: ${({ $side }) => ($side === 'left' ? 'flex-start' : 'center')};
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
  ${({ $side }) =>
    $side === 'left' ? 'flex: 1 1 auto;' : 'margin-left: auto;'}

  @media (max-width: 991px) {
    width: 100%;
    margin-left: 0;
  }
`;

const HeaderSearch = styled(Input.Search)`
  flex: 0 0 330px;
  width: 330px;
`;

function filterStringMatch(filter: unknown, record?: unknown): boolean {
  if (record == null) return false;
  return String(record)
    .toLowerCase()
    .includes(String(filter ?? '').toLowerCase());
}

interface CustomTableProps<T>
  extends Omit<TableProps<T>, 'dataSource' | 'columns' | 'title' | 'rowKey'> {
  dataSource: T[];
  columns: CustomColumns<T>;
  rowKey: keyof T;
  exportOptions?: TableExportOptions<T>;
  disabled?: boolean;
  headerActions?: {
    left?: React.ReactNode;
    right?: React.ReactNode;
  };
  onAdd?: () => void;
  /** Immediate inline edit; used when `deferInlineEdit` is false. */
  onUpdate?: (oldData: T, newData: Partial<T>) => void;
  /** Defer inline edits until the header Apply button is clicked. */
  deferInlineEdit?: boolean;
  applyLabel?: string;
  /**
   * Rows staged outside inline edits, such as an edit modal.
   * Keeps Apply visible and lets Apply run when only those rows changed.
   */
  externalPendingCount?: number;
  /** Batch apply; used when `deferInlineEdit` is true. */
  onApplyComplete?: (
    updates: DeferredInlineEditUpdate<T>[],
  ) => void | Promise<void>;
  onDeferredInlineChange?: (
    rowKey: string,
    record: T,
    changes: Partial<T>,
  ) => void;
  appSearchOptions?: {
    searchPlaceholder?: string;
    storageKey?: StorageKeys;
    /** Appended to storageKey for localStorage read/write of the active search (e.g. "-myView" with URL ?view=myView). */
    searchKeySuffix?: string;
    defaultSearch?: AppSearch<T>;
    value?: AppSearch<T>;
    onChange?: (query: AppSearch<T>) => void;
  };
}

export function CustomTable<T>(props: CustomTableProps<T>) {
  return (
    <DeferredInlineEditProvider
      enabled={!!props.deferInlineEdit}
      onPendingChange={props.onDeferredInlineChange}
    >
      <CustomTableInner {...props} />
    </DeferredInlineEditProvider>
  );
}

function CustomTableInner<T>({
  dataSource,
  columns,
  rowKey,
  exportOptions,
  disabled,
  headerActions,
  onAdd,
  onUpdate,
  deferInlineEdit,
  applyLabel = 'Apply',
  externalPendingCount = 0,
  onApplyComplete,
  appSearchOptions,
  onChange,
  pagination,
  loading,
  ...restProps
}: CustomTableProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [uniqueRowKey, setUniqueRowKey] = useState<string>();
  const [searchText, setSearchText] = useState<string>();
  const { t } = useTranslate();
  const theme = useTheme();
  const deferredEdit = useDeferredInlineEdit<T>();
  const [applying, setApplying] = useState(false);

  const exporting = exportOptions ? true : false;
  const appSearchStorage = appSearchOptions?.storageKey;
  const defaultAppSearch = appSearchOptions?.defaultSearch;
  const appSearch = appSearchOptions?.value;
  const onAppSearchChange = appSearchOptions?.onChange;
  const searchPlaceholder = appSearchOptions?.searchPlaceholder;
  const showQuickView = !!(appSearch && appSearchStorage);

  const handleApplyAll = useCallback(async () => {
    if (!onApplyComplete || !deferredEdit) return;
    setApplying(true);
    try {
      const updates = deferredEdit.peekPendingUpdates();
      if (updates.length === 0 && externalPendingCount <= 0) return;
      try {
        await onApplyComplete(updates);
        if (updates.length > 0) {
          deferredEdit.clearSubmittedUpdates(updates, rowKey);
        }
      } catch {
        // Leave the pending edits in place so the row keeps the new values.
      }
    } finally {
      setApplying(false);
    }
  }, [deferredEdit, externalPendingCount, onApplyComplete, rowKey]);

  const dateColumns = useMemo(
    () =>
      columns
        .filter((col) => col.renderType === 'date')
        .map((o) => o.dataIndex as keyof T),
    [columns],
  );

  const columnsWithFilter: QuickViewColumns<T>[] = useMemo(
    () =>
      columns.map((o) => ({
        key: o.dataIndex,
        label: o.titleString || (typeof o.title === 'string' ? o.title : ''),
        filterOptions: o.filterOptions,
        renderType: o.renderType,
      })),
    [columns],
  );

  useEffect(() => {
    let shouldUseIdx = false;
    if (!rowKey) {
      shouldUseIdx = true;
    } else {
      const rowKeyData = dataSource.map((o) => JSON.stringify(o[rowKey]));
      const hasDuplicates = rowKeyData.some((item, index) =>
        rowKeyData.some((elem, idx) => elem === item && idx !== index),
      );
      if (hasDuplicates) shouldUseIdx = true;
    }
    setUniqueRowKey(shouldUseIdx ? ('idx' as any) : rowKey);
    setData(
      shouldUseIdx
        ? dataSource.map((o, idx) => ({ ...o, idx }))
        : [...dataSource],
    );
  }, [rowKey, dataSource]);

  useEffect(() => {
    setSearchText(appSearch?.search ?? undefined);
  }, [appSearch?.search]);

  const sortIcon = useCallback(
    (sortOrder: SortOrder) =>
      sortOrder ? (
        <Icon
          icon={sortOrder === 'ascend' ? faArrowUp : faArrowDown}
          style={{ color: theme.accent }}
        />
      ) : null,
    [theme.accent],
  );

  const filterIcon = useCallback(
    (filtered: boolean, renderType?: RenderType) => (
      <Icon
        icon={
          renderType === 'select'
            ? faFilter
            : renderType === 'date'
              ? faCalendar
              : faSearch
        }
        style={{ color: filtered ? theme.accent : undefined }}
      />
    ),
    [theme.accent],
  );

  const customFilter = useCallback(
    (
      {
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        filters,
      }: FilterDropdownProps,
      renderType?: RenderType,
    ) => (
      <Space
        direction="vertical"
        style={{ padding: 8 }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {renderType === 'number' ? (
          <Space>
            <Select
              value={selectedKeys[1] || 'Equal'}
              onChange={(e) => setSelectedKeys([selectedKeys[0], e])}
              style={{ width: 80 }}
            >
              <Select.Option value="Equal">Equal</Select.Option>
              <Select.Option value="Not">Not</Select.Option>
              <Select.Option value="<=">{'<='}</Select.Option>
              <Select.Option value=">=">{'>='}</Select.Option>
            </Select>
            <InputNumber
              className="w-100"
              placeholder={t('search')}
              value={selectedKeys[0] as any}
              onChange={(e) =>
                setSelectedKeys(
                  e !== null ? [e, selectedKeys[1] || 'Equal'] : [],
                )
              }
              onPressEnter={() => confirm()}
            />
          </Space>
        ) : renderType === 'date' ? (
          <DatePicker.RangePicker
            value={selectedKeys[0] as any}
            onChange={(e: any) => setSelectedKeys(e ? [e] : [])}
            minDate={dayjs('2025-01-01')}
          />
        ) : renderType === 'select' ? (
          <Space>
            <Select
              value={
                selectedKeys.find((o) => o === 'Equal' || o === 'Not') ||
                'Equal'
              }
              onChange={(e) => {
                const newSelectedKeys = selectedKeys.filter(
                  (o) => o !== 'Equal' && o !== 'Not',
                );
                setSelectedKeys([...newSelectedKeys, e]);
              }}
              style={{ width: 80 }}
            >
              <Select.Option value="Equal">Equal</Select.Option>
              <Select.Option value="Not">Not</Select.Option>
            </Select>
            <Select
              value={selectedKeys.filter((o) => o !== 'Equal' && o !== 'Not')}
              options={filters?.map((o) => ({ label: o.text, value: o.value }))}
              onChange={(e) => {
                const newSelectedKeys = e.filter(
                  (o) => o !== 'Equal' && o !== 'Not',
                );
                const opertate = selectedKeys.find(
                  (o) => o === 'Equal' || o === 'Not',
                );
                setSelectedKeys([...newSelectedKeys, opertate || 'Equal']);
              }}
              style={{ width: 200 }}
              showSearch
              mode="multiple"
              filterOption={(input, option) =>
                ((option?.label as string) ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Space>
        ) : (
          <Space>
            <Select
              value={selectedKeys[1] || 'Contains'}
              onChange={(e) => setSelectedKeys([selectedKeys[0], e])}
              style={{ width: 100 }}
            >
              <Select.Option value="Contains">Contains</Select.Option>
              <Select.Option value="Not">Not</Select.Option>
            </Select>
            <Input
              placeholder={t('search')}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(
                  e.target.value
                    ? [e.target.value, selectedKeys[1] || 'Contains']
                    : [],
                )
              }
              onPressEnter={() => confirm()}
              allowClear
            />
          </Space>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <Button type="primary" onClick={() => confirm()} size="small">
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
            size="small"
          >
            Reset
          </Button>
        </div>
      </Space>
    ),
    [t],
  );

  const renderColumns = useMemo(
    () =>
      columns.map((oriCol) => {
        // Avoid mutating the `columns` prop object.
        const col: any = { ...oriCol };

        col.sortDirections = ['ascend', 'descend', 'ascend'];
        col.sortIcon = ({ sortOrder }: any) => sortIcon(sortOrder);
        col.sorter =
          typeof col.sorter === 'function'
            ? col.sorter
            : col.sortable || col.sorter
              ? (a: any, b: any) => baseSort(a, b, col.dataIndex)
              : false;
        col.filterIcon = (filtered: boolean) =>
          filterIcon(filtered, col.renderType);

        if (col.filter) {
          if (!col.onFilter) {
            col.onFilter = (value: any, record: any) => {
              // When `appSearch` is provided, actual filtering happens outside.
              if (appSearch) return true;

              const checkValue = record[col.dataIndex];
              const renderValue = col.render
                ? col.render(checkValue, record, 0)?.toString()
                : checkValue;

              return col.renderType === 'select'
                ? value === checkValue
                : col.renderType === 'date'
                  ? dayjs(renderValue as string).isBetween(
                      value[0],
                      value[1],
                      'day',
                    )
                  : filterStringMatch(value, renderValue);
            };
          }

          // Needed by `customFilter` when renderType === 'select'.
          if (col.renderType === 'select') {
            col.filters = col.filterOptions
              ? col.filterOptions
              : typeof col.renderOptions === 'function'
                ? []
                : (col.renderOptions?.map((option: any) => ({
                    text: option.label,
                    value: option.value,
                  })) as any);
          }

          col.filterDropdown = (p: FilterDropdownProps) =>
            customFilter(p, col.renderType);
        }

        if (appSearch) {
          col.sortOrder =
            appSearch.sortField === col.dataIndex ? appSearch.sortOrder : null;

          let filteredValue = appSearch.filters?.find(
            (f) => f.key === col.dataIndex,
          )?.value;

          if (filteredValue && col.renderType === 'date') {
            filteredValue = [filteredValue.map((v: any) => dayjs(v))];
          }

          col.filteredValue =
            filteredValue === undefined ? null : filteredValue;
        }

        return !col.editable
          ? col
          : {
              ...col,
              onCell: (record: T) => ({
                record,
                rowKey: String(record[rowKey]),
                editable: col.editable,
                renderType: col.renderType,
                renderOptions: col.renderOptions,
                optionsLoading: col.optionsLoading,
                rules: col.rules,
                exportRender: col.exportRender,
                dataIndex: col.dataIndex,
                clearFieldsOnEdit: col.clearFieldsOnEdit,
                onUpdate: deferInlineEdit ? undefined : onUpdate,
              }),
            };
      }),
    [
      appSearch,
      columns,
      customFilter,
      deferInlineEdit,
      filterIcon,
      onUpdate,
      rowKey,
      sortIcon,
    ],
  );

  const changeAppSearch = useCallback(
    (query: Partial<AppSearch<T>>) => {
      const newQuery = {
        ...(appSearch || {}),
        ...(query as any),
      } as AppSearch<T>;

      if (appSearchStorage) {
        if (newQuery.name) {
          window.localStorage.setItem(
            `${appSearchStorage}-view`,
            String(newQuery.name),
          );
          window.localStorage.setItem(
            `${appSearchStorage}-${String(newQuery.name)}`,
            JSON.stringify(newQuery),
          );
        } else {
          window.localStorage.removeItem(`${appSearchStorage}-view`);
        }
      }

      const suffixForWrite =
        appSearchStorage && newQuery.name ? `-${String(newQuery.name)}` : '';

      const persistenceKey = appSearchStorage
        ? `${appSearchStorage}${suffixForWrite}`
        : undefined;
      if (persistenceKey) {
        window.localStorage.setItem(persistenceKey, JSON.stringify(newQuery));
      }
      onAppSearchChange?.(newQuery);
    },
    [appSearch, appSearchStorage, onAppSearchChange],
  );

  const didHydrateSearchFromStorageRef = useRef(false);

  useEffect(() => {
    if (didHydrateSearchFromStorageRef.current) return;
    if (!appSearchStorage || !defaultAppSearch || !onAppSearchChange) return;
    if (appSearch !== undefined && appSearch !== null) return;

    didHydrateSearchFromStorageRef.current = true;

    const suffixForRead = appSearchStorage
      ? (() => {
          try {
            const v = window.localStorage.getItem(`${appSearchStorage}-view`);
            return v ? `-${v}` : '';
          } catch {
            return '';
          }
        })()
      : '';
    const readKey = `${appSearchStorage}${suffixForRead}`;

    let stored: Partial<AppSearch<T>> | null = null;
    try {
      const raw = window.localStorage.getItem(readKey);
      if (raw) {
        stored = JSON.parse(raw) as Partial<AppSearch<T>>;
      }
    } catch {
      stored = null;
    }

    const cachedQuery = {
      ...defaultAppSearch,
      ...(stored || {}),
    } as AppSearch<T>;
    if (!cachedQuery.page) {
      cachedQuery.page = 1;
    }
    if (!cachedQuery.pageSize) {
      cachedQuery.pageSize = defaultAppSearch.pageSize ?? 10;
    }
    changeAppSearch(cachedQuery as Partial<AppSearch<T>>);
  }, [
    appSearch,
    appSearchStorage,
    changeAppSearch,
    defaultAppSearch,
    onAppSearchChange,
  ]);

  const onSearchKeyChange = useCallback(
    (e?: string) => {
      const normalizedSearch = e?.trim() ? e.trim() : undefined;
      changeAppSearch?.({ search: normalizedSearch, page: 1 });
    },
    [changeAppSearch],
  );

  const onTableChange = useCallback(
    (paginationState: any, filters: any, sorter: any, extra: any) => {
      const sort = sorter;
      const queryFilters = Object.keys(filters)
        .map((key) => {
          const val = filters[key] ? filters[key]?.[0] : undefined;
          if (!val) return null;

          const filterKey = key as keyof T;
          const filterVal = dateColumns.includes(filterKey)
            ? [
                dayjs(val[0]).startOf('day').toDate(),
                dayjs(val[1]).endOf('day').toDate(),
              ]
            : filters[key];
          return {
            key: filterKey,
            value: filterVal,
          };
        })
        .filter((f) => f !== null);

      changeAppSearch?.({
        page: paginationState.current,
        pageSize: paginationState.pageSize,
        sortField: sort?.field ? (sort.field as keyof T) : undefined,
        sortOrder: sort?.field ? sort.order : undefined,
        filters: queryFilters as any,
      });

      onChange?.(paginationState, filters, sorter, extra);
    },
    [changeAppSearch, dateColumns, onChange],
  );

  const hasLeftHeader = showQuickView || !!headerActions?.left;
  const hasRightHeader =
    !!onAdd ||
    deferInlineEdit ||
    exporting ||
    !!appSearch ||
    !!headerActions?.right;
  const showApplyButton =
    !!deferInlineEdit &&
    (!!deferredEdit?.hasPendingChanges || externalPendingCount > 0);

  const tableBodyComponents = useMemo(
    () => ({
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    }),
    [],
  );

  const mergedPagination = useMemo(() => {
    if (pagination === false) return false;
    return {
      ...(pagination || {}),
      showTotal: pagination?.showTotal
        ? pagination.showTotal
        : (totalItem: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${totalItem}`,
      showSizeChanger: true,
    };
  }, [pagination]);

  return (
    <Spin spinning={!!loading}>
      {headerActions || onAdd || deferInlineEdit || appSearch || exporting ? (
        <HeaderToolbar>
          {hasLeftHeader ? (
            <HeaderSide $side="left">
              {headerActions?.left}
              {showQuickView ? (
                <div className="min-w-0" style={{ flex: '1 1 0%' }}>
                  <QuickViewPanel
                    columns={columnsWithFilter}
                    appSearchStorage={appSearchStorage!}
                    defaultAppSearch={defaultAppSearch}
                    appSearch={appSearch!}
                    changeAppSearch={changeAppSearch}
                  />
                </div>
              ) : null}
            </HeaderSide>
          ) : null}
          {hasRightHeader ? (
            <HeaderSide $side="right">
              {headerActions?.right}
              {exporting && exportOptions ? (
                <TableExportButton
                  disabled={disabled}
                  columns={columns}
                  dataSource={data}
                  exportOptions={exportOptions}
                />
              ) : null}
              {appSearch ? (
                <HeaderSearch
                  placeholder={
                    searchPlaceholder || 'Press Enter or click icon to search'
                  }
                  size="large"
                  minLength={3}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={(e) => onSearchKeyChange(e.trim())}
                  allowClear
                />
              ) : null}
              {showApplyButton ? (
                <Button
                  type="primary"
                  disabled={disabled || applying}
                  loading={applying}
                  onClick={handleApplyAll}
                >
                  {applyLabel}
                </Button>
              ) : null}
              {onAdd ? (
                <Button shape="circle" disabled={disabled} onClick={onAdd}>
                  <Icon icon={faAdd} />
                </Button>
              ) : null}
            </HeaderSide>
          ) : null}
        </HeaderToolbar>
      ) : null}
      <Table<T>
        components={tableBodyComponents}
        bordered
        dataSource={data}
        columns={renderColumns as ColumnType<T>[]}
        rowKey={uniqueRowKey}
        onChange={onTableChange}
        pagination={mergedPagination}
        {...restProps}
      />
    </Spin>
  );
}
